import { IconButton, InputAdornment, TextField } from "@mui/material";
import { ChangeEventHandler, KeyboardEventHandler } from "react";
import { UseFormRegister } from "react-hook-form";

export type TextInputProps = {
  label?: string;
  defaultValue?: string;
  id?: string;
  placeholder?: string;
  ariaLabel?: string;
  icon?: string | JSX.Element;
  iconHandler?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  handleChange?:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  type?: string;
  value?: string;
  adornment?: string;
  borderRadius?: string;
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  bgcolor?: string;
  color?: string;
  labelDisplay?: string;
  fontWeight?: string;
  fontSize?: string;
  borderColor?: string;
  height?: string;
  labelMargin?: string;
  icon2?: string | JSX.Element;
  handleKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  handleKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  shrink?: boolean;
  name?: string;
  register?: UseFormRegister<any>;
  required?: boolean;
  helperText?: string;
  pattern?: RegExp;
  textAlign?: string;
  maxLength?: number;
  multiline?: boolean;
  className?: string;
  width?: string;
  disabled?: boolean;
};

export default function TextInput({
  label,
  defaultValue,
  id,
  placeholder,
  ariaLabel,
  icon,
  iconHandler = () => {},
  handleChange = undefined,
  type,
  value,
  adornment = "endAdornment",
  borderRadius = "5px",
  borderTopLeftRadius,
  borderTopRightRadius,
  bgcolor = "initial",
  color = "#2A945F",
  labelDisplay = "block",
  fontWeight = "600",
  fontSize = "13px",
  borderColor = "rgba(52, 180, 116, 0.5)",
  height = "auto",
  labelMargin = "auto",
  icon2,
  name,
  register,
  handleKeyUp = () => {},
  handleKeyDown = () => {},
  shrink = undefined,
  helperText = "",
  required = false,
  pattern = /^/,
  textAlign = "left",
  maxLength = 1000000000000000,
  multiline = false,
  className = "",
  width = "100%",
  disabled = false,
}: TextInputProps) {
  const registerProps =
    register && name
      ? {
          ...register(name, pattern ? { required, pattern } : { required }),
        }
      : {};

  return (
    <TextField
      disabled={disabled}
      type={type}
      value={value}
      defaultValue={defaultValue}
      id={id}
      label={label}
      multiline={multiline}
      variant="outlined"
      placeholder={placeholder}
      InputLabelProps={{
        shrink,
        style: {
          color: "#2A945F",
          fontFamily: "Poppins, Poppins Fallback",
          fontSize: "14px",
          display: labelDisplay,
          padding: ".3rem !important",
          transform: "translate(-14px, -14px) scale(0.7) !important",
          margin: labelMargin,
        },
      }}
      {...registerProps}
      slotProps={{
        htmlInput: maxLength ? { maxLength } : {},
        input:
          icon || icon2
            ? {
                [adornment]: (
                  <InputAdornment position="start">
                    {labelMargin != "auto" ? (
                      <IconButton
                        aria-label={ariaLabel}
                        onClick={iconHandler}
                        edge="end"
                        sx={{ marginRight: "4px" }}
                      >
                        {icon2}
                      </IconButton>
                    ) : null}
                    <IconButton
                      aria-label={ariaLabel}
                      onClick={iconHandler}
                      edge="end"
                      sx={{
                        backgroundColor:
                          labelMargin != "auto" ? "#2a945f" : "initial",
                      }}
                    >
                      {icon}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : {},
      }}
      {...(handleChange ? { onChange: handleChange } : {})}
      onKeyUp={handleKeyUp}
      onKeyDown={handleKeyDown}
      size="small"
      sx={{
        "& .MuiInputBase-input": {
          textAlign: textAlign || "left",
          width,
          color,
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: "white", // your desired color
        },

        "& input[type=number]::-webkit-outer-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        "& input[type=number]::-webkit-inner-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        "& .MuiFormHelperText-root": {
          color: "#e11c1cb3 !important",
          marginTop: "0px !important",
          height: !name?.toLowerCase()?.includes("password") ? "10px" : "auto",
        },
        "&.MuiFormControl-root": {
          width,
        },
        "& .MuiOutlinedInput-root": {
          // color: "white",
          // fontWeight: "bold",
          backgroundColor: bgcolor,
          height,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor,
            borderWidth: "2px",
            borderRadius,
            borderTopLeftRadius: borderTopLeftRadius || borderRadius,
            borderTopRightRadius: borderTopRightRadius || borderRadius,
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor,
              borderWidth: "2px",
            },
            "& .MuiInputLabel": {
              color: "red !important",
              display: "none",
            },
          },
          "&:hover fieldset": {
            borderColor,
          },

          "& input::placeholder": {
            fontWeight,
            fontSize,
            color,
          },
          "& input": {
            height,
          },
        },
      }}
      helperText={helperText}
      className={className}
    />
  );
}
